import { error, log, warn } from "./logger";

// get size bit to kb and append 'kb' to it
export function getSize(size: number) {
  return (size / 1024).toFixed(2) + " kB";
}

// return sample rate into ration 40 into 0.4
export function getSampleRateRatio(sampleRate?: number) {
  let defaultSampleRate = 40;
  if (sampleRate && sampleRate >= 1 && sampleRate <= 100) {
    defaultSampleRate = sampleRate;
  }
  return defaultSampleRate / 100;
}

// get path after last slash
export function getLastPathWithQuery(urlString: string) {
  try {
    const url = new URL(urlString, "http://dummybase");
    const pathSegments = url.pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1] || "";
    if (lastSegment === "") {
      return url.pathname + url.search;
    }
    return lastSegment + url.search;
  } catch (e) {
    log(e);
    error("Invalid URL:", urlString);
    return urlString;
  }
}

// fold paths on UUIDs and numeric IDs with '?'
export function getFoldedPath(pathname: string): string {
  if (pathname === "/") return "/";

  // Normalize: replace multiple slashes with a single slash, remove trailing slashes (except root)
  let normalized = pathname.replace(/\/+/g, "/");
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.replace(/\/+$/, "");
  }

  // Split the path into segments
  const segments = normalized.split("/");
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const numberRegex = /^\d+$/;
  const folded = segments.map((seg) => {
    if (uuidRegex.test(seg) || numberRegex.test(seg)) {
      return "?";
    }
    return seg;
  });

  // Remove empty segments except for root
  const result = folded.filter((seg, idx) => seg !== "" || idx === 0).join("/");
  return result;
}

/**
 * Listen for navigation events (SPA and traditional) and call the callback when pathname or hash changes.
 * Ignores search param changes. Debounced to avoid duplicate calls.
 */
export function onNavigation(callback: () => void) {
  let lastPath = window.location.pathname;
  let lastHash = window.location.hash;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const handleNavigation = () => {
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    if (currentPath !== lastPath || currentHash !== lastHash) {
      lastPath = currentPath;
      lastHash = currentHash;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        callback();
      }, 10);
    }
  };

  try {
    window.addEventListener("popstate", handleNavigation);
    window.addEventListener("hashchange", handleNavigation);
  } catch (e) {
    warn("[L9RUM Utils] Failed to attach popstate/hashchange listeners", e as any);
  }

  // Monkey-patch pushState and replaceState for SPA navigation
  try {
    const origPushState = history.pushState;
    const origReplaceState = history.replaceState;
    history.pushState = function (
      this: History,
      ...args: Parameters<History["pushState"]>
    ): ReturnType<History["pushState"]> {
      const result = origPushState.apply(this, args as any);
      try {
        window.dispatchEvent(new Event("l9rum:navigation"));
      } catch (e) {
        warn("[L9RUM Utils] Failed to dispatch l9rum:navigation after pushState", e as any);
      }
      return result as ReturnType<History["pushState"]>;
    } as History["pushState"];
    history.replaceState = function (
      this: History,
      ...args: Parameters<History["replaceState"]>
    ): ReturnType<History["replaceState"]> {
      const result = origReplaceState.apply(this, args as any);
      try {
        window.dispatchEvent(new Event("l9rum:navigation"));
      } catch (e) {
        warn("[L9RUM Utils] Failed to dispatch l9rum:navigation after replaceState", e as any);
      }
      return result as ReturnType<History["replaceState"]>;
    } as History["replaceState"];
    window.addEventListener("l9rum:navigation", handleNavigation);
  } catch (e) {
    warn("[L9RUM Utils] Failed to patch history state", e as any);
  }
}
